import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { UserAvatar, AvatarStack } from '@/components/UserAvatar';
import { notifyNewMessage, ensureNotificationPermission } from '@/utils/messageNotifications';
import {
  Send, ImagePlus, Users, Plus, ArrowLeft, X, Search,
  MessageCircle, Download, FileText, FileDown, UserPlus, Hash,
  Settings, Trash2, Phone, ChevronDown, Check,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ============ Image component (fetches blob) ============
const ChatImage = ({ path, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    fetch(`${API}/api/chat/image/${path}`, { headers: authHeaders() })
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (b) setSrc(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [path]);
  if (!src) return <div className="w-48 h-32 rounded-lg bg-muted/30 animate-pulse" />;
  return <img src={src} alt={alt || 'image'} className="max-w-[280px] max-h-[220px] rounded-lg object-cover cursor-pointer border border-muted/50" onClick={() => window.open(src)} />;
};

// ============ New Conversation / Group Dialog ============
const NewConvoDialog = ({ onClose, onCreated, token, currentUserId, isAdmin }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/chat/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
  }, [token]);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (uid) => setSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);

  const create = async () => {
    if (!selected.length) { toast.error('Select at least one user'); return; }
    if (isGroup && !groupName.trim()) { toast.error('Group name required'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ members: [...selected, currentUserId], is_group: isGroup, name: groupName.trim() }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to create conversation');
      }
      const convo = await r.json();
      onCreated(convo.conversation_id);
    } catch (e) { toast.error(e.message || 'Failed to create conversation'); }
    setCreating(false);
  };

  // Combobox popover state for the recipients dropdown.
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="absolute inset-0 bg-background z-10 flex flex-col" data-testid="new-convo-dialog">
      <div className="p-4 border-b border-muted/50 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}><ArrowLeft className="h-5 w-5" /></Button>
        <h3 className="text-lg font-semibold">{isGroup ? 'New Group' : 'New Conversation'}</h3>
        <div className="ml-auto flex gap-2">
          {isAdmin && (
            <Button variant={isGroup ? 'default' : 'outline'} size="sm" onClick={() => setIsGroup(true)} data-testid="new-group-toggle">
              <Users className="h-4 w-4 mr-1" />Group
            </Button>
          )}
          <Button variant={!isGroup ? 'default' : 'outline'} size="sm" onClick={() => setIsGroup(false)} data-testid="new-dm-toggle">
            <MessageCircle className="h-4 w-4 mr-1" />Direct
          </Button>
        </div>
      </div>

      {/* Compact centered card — keeps the dialog focused on its one
          job (pick recipients + start) instead of sprawling a list
          across the whole viewport. */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-lg ml-auto mr-0 md:mr-12 space-y-4" data-testid="new-convo-card">
          {isGroup && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Group name</Label>
              <Input
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="e.g. Shift leaders"
                data-testid="group-name-input"
              />
            </div>
          )}

          {/* Recipients dropdown — filter field lives inside the popover,
              so the outer layout stays calm and fits on a phone screen. */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {isGroup ? 'Members' : 'Recipient'}
            </Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between h-10 font-normal"
                  data-testid="recipients-trigger"
                >
                  <span className="truncate text-left">
                    {selected.length === 0
                      ? (isGroup ? 'Select members…' : 'Select a recipient…')
                      : `${selected.length} selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid="recipients-popover">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search users…"
                      className="pl-8 h-8"
                      data-testid="user-search"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[320px] overflow-y-auto py-1">
                  {filtered.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">No users found</p>
                  )}
                  {filtered.map(u => {
                    const isOn = selected.includes(u.user_id);
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        onClick={() => {
                          // For a 1-on-1 DM, clicking a user replaces
                          // the selection; for a group we toggle.
                          if (isGroup) toggle(u.user_id);
                          else {
                            setSelected(isOn ? [] : [u.user_id]);
                            setPickerOpen(false);
                          }
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 ${isOn ? 'bg-primary/5' : ''}`}
                        data-testid={`user-option-${u.user_id}`}
                      >
                        {isGroup && (
                          <Checkbox checked={isOn} className="pointer-events-none" />
                        )}
                        <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-[11px] font-bold text-primary">{(u.name || '?')[0].toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{u.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                        </div>
                        {!isGroup && isOn && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* Selected chip row — gives a visible confirmation without
                forcing the user to reopen the popover. */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2" data-testid="selected-recipients">
                {selected.map(id => {
                  const u = users.find(u => u.user_id === id);
                  return (
                    <Badge key={id} className="gap-1 cursor-pointer" onClick={() => toggle(id)}>
                      {u?.name || id} <X className="h-3 w-3" />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          <Button onClick={create} disabled={creating} className="w-full" data-testid="create-convo-btn">
            {creating ? 'Creating…' : isGroup ? 'Create Group' : 'Start Conversation'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ Message Area ============
const MessageArea = ({ convoId, convo, token, userId, onBack, ws, isAdmin, onOpenGroupSettings }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const loadMsgs = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convoId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setMessages(await r.json());
    } catch {}
  }, [convoId, token]);

  useEffect(() => { loadMsgs(); }, [loadMsgs]);
  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Poll for new messages as fallback
  useEffect(() => {
    const interval = setInterval(loadMsgs, 5000);
    return () => clearInterval(interval);
  }, [loadMsgs]);

  // Listen for new messages via WebSocket
  useEffect(() => {
    if (!ws) return;
    const handler = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'new_message' && data.conversation_id === convoId) {
          setMessages(prev => {
            if (prev.some(m => m.message_id === data.message.message_id) ) return prev;
            return [...prev, data.message];
          });
        }
        // Remove a message that was deleted on the other side so the
        // chat stays in sync without a hard reload.
        if (data.type === 'message_deleted' && data.conversation_id === convoId) {
          setMessages(prev => prev.filter(m => m.message_id !== data.message_id));
        }
      } catch {}
    };
    ws.addEventListener('message', handler);
    return () => ws.removeEventListener('message', handler);
  }, [ws, convoId]);

  // Delete a single message — optimistic update + server confirm.
  // Server enforces "sender or admin" so we only render the button
  // for those users; non-authorised users will never see it.
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message for both sides? This cannot be undone.')) return;
    const prev = messages;
    setMessages(prev.filter(m => m.message_id !== messageId));
    try {
      const r = await fetch(`${API}/api/chat/messages/${messageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to delete message');
      }
      toast.success('Message deleted');
    } catch (e) {
      // Roll back the optimistic update if the server rejected it.
      setMessages(prev);
      toast.error(e.message || 'Failed to delete message');
    }
  };

  const sendMsg = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convoId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: input.trim(), message_type: 'text' }),
      });
      if (r.ok) {
        const msg = await r.json();
        setMessages(prev => prev.some(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
        setInput('');
      }
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const upR = await fetch(`${API}/api/chat/upload-image`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!upR.ok) { const e = await upR.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      const { path, original_name } = await upR.json();

      const r = await fetch(`${API}/api/chat/conversations/${convoId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: '', message_type: 'image', image_path: path, image_name: original_name }),
      });
      if (r.ok) {
        const msg = await r.json();
        setMessages(prev => prev.some(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
      }
    } catch (e) { toast.error(e.message || 'Image upload failed'); }
    setUploading(false);
  };

  const exportChat = async (fmt) => {
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convoId}/export/${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = r.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="(.+?)"/);
      a.download = match ? match[1] : `chat.${fmt === 'word' ? 'docx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt === 'word' ? 'Word' : 'PDF'}`);
    } catch { toast.error('Export failed'); }
  };

  const otherName = convo?.is_group ? convo.name : convo?.member_details?.find(m => m.user_id !== userId)?.name || 'Chat';
  const memberCount = convo?.members?.length || 0;

  return (
    <div className="flex flex-col h-full" data-testid="message-area">
      {/* Header */}
      <div className="px-4 py-3 border-b border-muted/50 flex items-center gap-3 flex-shrink-0">
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: convo?.is_group ? 'rgb(45,140,140,0.15)' : 'rgb(99,102,241,0.15)' }}>
          {convo?.is_group ? <Users className="h-4 w-4 text-teal-500" /> : <span className="text-sm font-bold text-indigo-400">{(otherName[0] || '?').toUpperCase()}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" data-testid="chat-title">{otherName}</p>
          <p className="text-xs text-muted-foreground">{convo?.is_group ? `${memberCount} members` : 'Direct message'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => exportChat('pdf')} data-testid="chat-export-pdf"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
        <Button variant="outline" size="sm" onClick={() => exportChat('word')} data-testid="chat-export-word"><FileText className="h-4 w-4 mr-1" />Word</Button>
        {convo?.is_group && (isAdmin || convo?.created_by === userId) && (
          <Button variant="outline" size="icon" onClick={onOpenGroupSettings} data-testid="group-settings-btn" title="Group settings">
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="messages-list">
        {messages.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No messages yet. Say hello!</p>
          </div>
        )}
        {messages.map(m => {
          const isMine = m.sender_id === userId;
          const canDelete = isMine || isAdmin;
          return (
            <div key={m.message_id} className={`group flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.message_id}`}>
              {/* Trash on hover for mine (left side of bubble) — keeps
                  the bubble itself uncluttered. Server enforces
                  permissions so a savvy user can't bypass this. */}
              {isMine && canDelete && (
                <button
                  type="button"
                  onClick={() => deleteMessage(m.message_id)}
                  className="hidden group-hover:flex items-center justify-center self-center h-7 w-7 mr-1 rounded-md text-muted-foreground hover:text-rose-500 hover:bg-rose-50"
                  data-testid={`msg-delete-${m.message_id}`}
                  title="Delete for both sides"
                  aria-label="Delete message"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
              <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
                {!isMine && <p className="text-xs font-medium text-primary mb-0.5 ml-1">{m.sender_name}</p>}
                <div className={`px-3.5 py-2 rounded-2xl ${isMine ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted/40 text-foreground rounded-bl-md'}`}>
                  {m.message_type === 'image' && m.image_path && <ChatImage path={m.image_path} alt={m.image_name} />}
                  {m.content && <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                </div>
                <p className={`text-[10px] text-muted-foreground mt-0.5 ${isMine ? 'text-right mr-1' : 'ml-1'}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {/* Admins can also delete other people's messages —
                  trash renders on the right of incoming bubbles so it
                  never overlaps the avatar. */}
              {!isMine && canDelete && (
                <button
                  type="button"
                  onClick={() => deleteMessage(m.message_id)}
                  className="hidden group-hover:flex items-center justify-center self-center h-7 w-7 ml-1 rounded-md text-muted-foreground hover:text-rose-500 hover:bg-rose-50"
                  data-testid={`msg-delete-${m.message_id}`}
                  title="Delete for both sides (admin)"
                  aria-label="Delete message"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
        <div ref={scrollRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-muted/50 flex items-center gap-2 flex-shrink-0">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value = ''; }} />
        <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-image-btn">
          <ImagePlus className={`h-5 w-5 ${uploading ? 'animate-pulse' : ''}`} />
        </Button>
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
          placeholder="Type a message..."
          className="flex-1"
          data-testid="chat-input"
        />
        <Button size="icon" onClick={sendMsg} disabled={sending || !input.trim()} data-testid="chat-send-btn">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ============ Conversation List ============
const ConvoList = ({ conversations, onSelect, activeId, userId, onDeleteThread }) => (
  <div className="space-y-1 overflow-y-auto" data-testid="convo-list">
    {conversations.map(c => {
      const isGroup = c.is_group;
      const otherName = isGroup ? c.name : c.member_details?.find(m => m.user_id !== userId)?.name || 'User';
      const isActive = c.conversation_id === activeId;
      return (
        <div
          key={c.conversation_id}
          onClick={() => onSelect(c.conversation_id)}
          className={`group p-3 rounded-xl cursor-pointer transition-colors flex items-center gap-3 ${isActive ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/30'}`}
          data-testid={`convo-item-${c.conversation_id}`}
        >
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isGroup ? 'rgb(45,140,140,0.15)' : 'rgb(99,102,241,0.15)' }}>
            {isGroup ? <Users className="h-4 w-4 text-teal-500" /> : <span className="text-xs font-bold text-indigo-400">{(otherName[0] || '?').toUpperCase()}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate">{otherName}</p>
              {isGroup && <Hash className="h-3 w-3 text-muted-foreground" />}
            </div>
            {c.last_message && <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>}
          </div>
          {c.last_message_at && (
            <span className="text-[10px] text-muted-foreground group-hover:hidden">
              {new Date(c.last_message_at).toLocaleDateString()}
            </span>
          )}
          {/* Per-row trash — hidden until row hover so a casual scan
              of the list stays clean. One click → AlertDialog with
              the consequence spelled out, no chained confirms. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDeleteThread(c); }}
            className="hidden group-hover:flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-rose-500 hover:bg-rose-50"
            data-testid={`convo-delete-${c.conversation_id}`}
            title={isGroup ? 'Delete group (admin only)' : 'Delete this thread for both sides'}
            aria-label="Delete thread"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      );
    })}
  </div>
);

// ============ Group Settings Dialog ============
const GroupSettingsDialog = ({ convo, token, onClose, onUpdated, onDeleted, currentUserId }) => {
  const [name, setName] = useState(convo?.name || '');
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/chat/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setAllUsers).catch(() => {});
  }, [token]);

  const memberIds = new Set((convo?.member_details || []).map(m => m.user_id));
  const filteredNonMembers = allUsers.filter(u => !memberIds.has(u.user_id) && (u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())));

  const rename = async () => {
    if (!name.trim()) { toast.error('Name cannot be empty'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convo.conversation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Rename failed'); }
      const updated = await r.json();
      onUpdated(updated);
      toast.success('Group renamed');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const addMember = async (uid) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convo.conversation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ add_members: [uid] }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Add failed'); }
      const updated = await r.json();
      onUpdated(updated);
      toast.success('Member added');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const removeMember = async (uid) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convo.conversation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ remove_members: [uid] }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Remove failed'); }
      const updated = await r.json();
      onUpdated(updated);
      toast.success('Member removed');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const deleteGroup = async () => {
    if (!window.confirm(`Delete group "${convo.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convo.conversation_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Delete failed'); }
      onDeleted();
      toast.success('Group deleted');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="absolute inset-0 bg-background z-20 flex flex-col" data-testid="group-settings-dialog">
      <div className="p-4 border-b border-muted/50 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}><ArrowLeft className="h-5 w-5" /></Button>
        <h3 className="text-lg font-semibold">Group Settings</h3>
        <Button variant="ghost" size="icon" className="ml-auto text-rose-500 hover:bg-rose-50" onClick={deleteGroup} disabled={saving} data-testid="group-delete-btn">
          <Trash2 className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Group Name</label>
          <div className="flex gap-2">
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="group-name-input" />
            <Button onClick={rename} disabled={saving || name.trim() === convo.name} data-testid="group-rename-save">Save</Button>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Members ({convo.member_details?.length || 0})
          </p>
          <div className="space-y-1 rounded-lg border border-muted/50">
            {(convo.member_details || []).map(m => (
              <div key={m.user_id} className="flex items-center gap-3 p-2.5 border-b border-muted/30 last:border-b-0" data-testid={`group-member-${m.user_id}`}>
                <UserAvatar user={m} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                {m.user_id === convo.created_by && <Badge variant="secondary" className="text-[10px]">Creator</Badge>}
                {m.user_id === currentUserId && <Badge variant="outline" className="text-[10px]">You</Badge>}
                {m.user_id !== convo.created_by && (
                  <Button variant="ghost" size="icon" onClick={() => removeMember(m.user_id)} disabled={saving} data-testid={`group-remove-${m.user_id}`}>
                    <X className="h-4 w-4 text-rose-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Add members</p>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user..." className="mb-2" />
          <div className="max-h-60 overflow-y-auto space-y-1 rounded-lg border border-muted/50">
            {filteredNonMembers.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3 text-center">No other users to add</p>
            ) : filteredNonMembers.map(u => (
              <div key={u.user_id} className="flex items-center gap-3 p-2.5 border-b border-muted/30 last:border-b-0">
                <UserAvatar user={u} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => addMember(u.user_id)} disabled={saving} data-testid={`group-add-${u.user_id}`}>
                  <UserPlus className="h-3.5 w-3.5 mr-1" />Add
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Main Chat Page ============
export const ChatPage = () => {
  const { user, isAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const activeConvoRef = useRef(null);
  const token = getToken();
  const userId = user?.user_id;
  const hasPhone = !!(user?.phone && String(user.phone).trim().length > 0);

  useEffect(() => { activeConvoRef.current = activeConvo; }, [activeConvo]);

  // Ask for Notification permission once on mount (only if phone is set and browser supports it)
  useEffect(() => {
    if (!hasPhone) return;
    ensureNotificationPermission().catch(() => {});
  }, [hasPhone]);

  const loadConvos = useCallback(async () => {
    if (!hasPhone) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/chat/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setConversations(await r.json());
    } catch {}
    setLoading(false);
  }, [token, hasPhone]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  // Delete an entire thread (DM = hard-delete for both sides, group =
  // soft-delete admin/creator-only). Server enforces permissions; the
  // UI just optimistically removes the row and rolls back on failure.
  const deleteThread = useCallback(async (convo) => {
    const isGroup = convo?.is_group;
    const otherName = isGroup
      ? (convo.name || 'this group')
      : (convo.member_details?.find(m => m.user_id !== userId)?.name || 'this person');
    const msg = isGroup
      ? `Delete the group "${otherName}"? Members will lose access immediately.`
      : `Delete the entire thread with ${otherName}? Every message will be removed for both sides — this cannot be undone.`;
    if (!window.confirm(msg)) return;

    const previous = conversations;
    setConversations(prev => prev.filter(c => c.conversation_id !== convo.conversation_id));
    if (activeConvo === convo.conversation_id) setActiveConvo(null);
    try {
      const r = await fetch(`${API}/api/chat/conversations/${convo.conversation_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to delete thread');
      }
      toast.success(isGroup ? 'Group deleted' : 'Thread deleted');
    } catch (e) {
      setConversations(previous);
      toast.error(e.message || 'Failed to delete thread');
    }
  }, [conversations, activeConvo, token, userId]);

  // WebSocket connection with polling fallback
  useEffect(() => {
    if (!token || !hasPhone) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    let ws;
    let wsConnected = false;
    const conversationsCache = {};
    try {
      ws = new WebSocket(`${wsUrl}/api/ws/chat?token=${token}`);
      ws.onopen = () => { wsRef.current = ws; wsConnected = true; };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'conversation_created' || data.type === 'new_message' || data.type === 'conversation_updated' || data.type === 'conversation_deleted') {
            loadConvos();
          }
          if (data.type === 'new_message' && data.message && data.message.sender_id !== userId) {
            // Sound + banner
            const isActive = activeConvoRef.current === data.conversation_id && document.visibilityState === 'visible' && document.hasFocus();
            if (!isActive) {
              const title = data.message.sender_name ? `Message from ${data.message.sender_name}` : 'New message';
              const body = data.message.message_type === 'image' ? '[Image]' : (data.message.content || '').slice(0, 140);
              notifyNewMessage({
                title,
                body,
                onClick: () => { setActiveConvo(data.conversation_id); },
              });
            }
          }
        } catch {}
      };
      ws.onerror = () => { wsConnected = false; };
      ws.onclose = () => { wsRef.current = null; wsConnected = false; };
    } catch { wsConnected = false; }

    // Polling fallback (every 5s) when WebSocket fails
    const pollInterval = setInterval(() => {
      if (!wsConnected) loadConvos();
    }, 5000);

    // Ping to keep alive
    const pingInterval = setInterval(() => { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); }, 30000);
    return () => { clearInterval(pollInterval); clearInterval(pingInterval); ws?.close(); };
  }, [token, loadConvos, userId, hasPhone]);

  const activeConvoData = conversations.find(c => c.conversation_id === activeConvo);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-[calc(100vh-80px)]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  // ========= Phone-number gate =========
  if (!hasPhone) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto mt-16 text-center space-y-5 p-8 rounded-2xl border border-muted/50 bg-card" data-testid="messages-phone-gate">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <Phone className="h-8 w-8 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Phone number required</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Messages feature is available to users with a verified phone number on file.
              Add yours in your profile to start chatting.
            </p>
          </div>
          <Link to="/profile">
            <Button data-testid="messages-add-phone-btn"><Phone className="h-4 w-4 mr-2" />Add phone number</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-80px)] flex rounded-xl border border-muted/50 overflow-hidden bg-card" data-testid="chat-page">
        {/* Sidebar */}
        <div className={`w-80 border-r border-muted/50 flex flex-col flex-shrink-0 ${activeConvo ? 'hidden lg:flex' : 'flex'}`}>
          <div className="p-4 border-b border-muted/50 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Messages</h2>
            <Button size="icon" variant="outline" onClick={() => setShowNew(true)} data-testid="new-convo-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {conversations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs mt-1">Start a new chat</p>
              </div>
            ) : (
              <ConvoList conversations={conversations} onSelect={(id) => { setActiveConvo(id); }} activeId={activeConvo} userId={userId} onDeleteThread={deleteThread} />
            )}
          </div>
          {showNew && (
            <NewConvoDialog
              onClose={() => setShowNew(false)}
              onCreated={(id) => { setShowNew(false); setActiveConvo(id); loadConvos(); }}
              token={token}
              currentUserId={userId}
              isAdmin={isAdmin}
            />
          )}
        </div>

        {/* Main area */}
        <div className={`flex-1 flex flex-col relative ${!activeConvo ? 'hidden lg:flex' : 'flex'}`}>
          {activeConvo && activeConvoData ? (
            <MessageArea
              convoId={activeConvo}
              convo={activeConvoData}
              token={token}
              userId={userId}
              isAdmin={isAdmin}
              onBack={() => setActiveConvo(null)}
              ws={wsRef.current}
              onOpenGroupSettings={() => setShowGroupSettings(true)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm mt-1">or start a new one</p>
              </div>
            </div>
          )}
          {showGroupSettings && activeConvoData && activeConvoData.is_group && (
            <GroupSettingsDialog
              convo={activeConvoData}
              token={token}
              currentUserId={userId}
              onClose={() => setShowGroupSettings(false)}
              onUpdated={() => loadConvos()}
              onDeleted={() => { setShowGroupSettings(false); setActiveConvo(null); loadConvos(); }}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatPage;
